import "./Login.css";
import React, { useEffect, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate from v6
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, provider, db } from "../base.js";
import { AuthContext } from "../Auth.js";

import { AiOutlineLeft as BackIcon } from "react-icons/ai";
import { MdEmail } from "react-icons/md";
import { FcGoogle } from "react-icons/fc";
import { SyncLoader } from "react-spinners";

import Button from "../components/Button";
import { validateEmail } from "../utils/validateEmail";

// Friendly messages for Firebase Auth errors. With the project set to "one
// account per email", registering a second provider for an existing email
// surfaces as account-exists-with-different-credential (Google) or
// email-already-in-use (email/password) — both mean "you already have an
// account; sign in with the method you first used". Returns null for benign
// cases (e.g. the user closed the Google popup) so callers show nothing.
function authErrorMessage(error) {
  switch (error?.code) {
    case "auth/account-exists-with-different-credential":
      return (
        "An account already exists for this email using a different sign-in " +
        "method. Please sign in the way you first registered (email/password " +
        "vs. Google)."
      );
    case "auth/email-already-in-use":
      return (
        "An account already exists for this email. Please sign in instead of " +
        "registering."
      );
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Your password must be at least 6 characters.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return null;
    default:
      return "Something went wrong. Please try again.";
  }
}

// Persist the user's Terms & Conditions acceptance on their `users` doc. The
// doc is lazily created in Auth.js on auth load and may not exist yet at
// sign-up time, so use setDoc + merge to create-or-update without clobbering
// other fields (and without depending on write ordering vs. onAuthStateChanged).
async function recordTermsAcceptance(user) {
  if (!user) {
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid),
      { terms_accepted: true },
      { merge: true },
    );
  } catch (e) {
    // Don't block the user on a failed write — they're already registered.
    console.error("Failed to record terms acceptance", e);
  }
}

// Terms shown on the sign-up / login landing. Adapted to a structured list so
// copy edits stay in one place rather than buried in JSX.
const TERMS = [
  "All software and artwork are owned and copyrighted by Piggyback, and not to be reproduced in any way.",
  "All payments are to be made in advance, with the option to trial the program before buying. Once payment has been made refunds are not warranted.",
  "Items damaged in post are not covered under warranty. Postal insurance is available upon request, at an additional fee. If you wish to opt in, please contact us at reframingliteracy@gmail.com",
  "This program is designed to be used by a Registered Optometrist or Vision Therapist.",
  "Videos and mentoring are available within the premium membership only, and shall not be copied, shared or distributed in any way.",
  "We are not liable for any injury caused during the implementation of our games on this website.",
  "Program subscriptions run for 365 days. At the end of that time, you will receive a notification with a short grace period to renew at a discounted rate. If you choose not to renew your subscription, access ends accordingly.",
  "Piggyback Parents does not have the option to renew.",
  "We retain the right to cancel subscriptions at any given time if we can prove within reason that our program has been stolen, copied or shared outside of your organization.",
];

const TermsAndConditions = ({ accepted, onAccept }) => (
  <div id="termsContainer">
    <h2 id="termsHeading">Terms and Conditions</h2>
    <p id="termsIntro">
      By visiting our website or signing up to our program you agree to adhere
      to our terms and conditions:
    </p>
    <ul id="termsList">
      {TERMS.map((term, i) => (
        <li key={i}>{term}</li>
      ))}
    </ul>
    <label id="termsAcceptLabel">
      <input
        type="checkbox"
        checked={accepted}
        onChange={(e) => onAccept(e.target.checked)}
      />
      <span>I have read and agree to the Terms and Conditions.</span>
    </label>
  </div>
);

export const SelectLoginType = (props) => {
  const navigate = useNavigate();

  const handleGoogleLogin = useCallback(async (event) => {
    event.preventDefault();
    try {
      // Must await so a rejected popup (e.g. account-exists-with-different-
      // credential when "one account per email" is on) is actually caught.
      // Terms consent is captured after sign-in via the first-login gate, so
      // returning users aren't asked to re-accept on every login.
      await signInWithPopup(auth, provider);
    } catch (error) {
      const msg = authErrorMessage(error);
      if (msg) {
        alert(msg);
      }
    }
  }, []);

  return (
    <>
      <div id="loginHeader">
        <div id="loginHeaderText">
          <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>Log In</h1>
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            Please log in to use piggyback or make purchases.
          </p>
        </div>
        <div onClick={() => navigate("/")} id="backButtonContainer">
          <BackIcon size={20} onClick={() => navigate("/")} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Back</p>
        </div>
      </div>
      <div id="loginBody">
        <button style={{width:"300px"}} onClick={handleGoogleLogin} id="googleLoginButton">
          <FcGoogle size={25} />
          <p style={{ color: "#434343", marginTop: "15px", fontWeight: "700" }}>
            Sign In/Register with Google
          </p>
        </button>
        <button
          style={{width:"300px"}}
          onClick={() => props.setLoginWithEmail(true)}
          id="emailLoginButton"
        >
          <MdEmail color="white" size={25} />
          <p style={{ color: "white", marginTop: "15px", fontWeight: "700" }}>
            Sign In/Register with Email
          </p>
        </button>
        <p
          style={{
            paddingTop: "2rem",
            fontFamily: "Mulish",
            fontSize: "16px",
            fontWeight: "700",
          }}
        >
          Don't have an account? Please create your account here.
        </p>
        <p
          className="forgotPasswordLink"
          onClick={() => props.onForgot()}
          style={{ paddingTop: "0.5rem" }}
        >
          Forgot your password?
        </p>
      </div>
    </>
  );
};

const EmailLogin = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleEmailLogin = useCallback(
    async (event) => {
      event.preventDefault();
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        const msg = authErrorMessage(error);
        if (msg) {
          alert(msg);
        }
      }
    },
    [email, password], // Use navigate as a dependency instead of history
  );

  const handleEmailSignUp = useCallback(
    async (event) => {
      event.preventDefault();
      if (!validateEmail(email)) {
        alert("Please enter a valid email address.");
        return;
      }
      try {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        // claimAccess (Auth.js) denies access until the email is verified, so a
        // verification email is mandatory — Firebase does NOT send one on its
        // own. The signed-in-but-unverified user lands on the VerifyEmail screen.
        // Terms consent is captured after verification via the first-login gate.
        await sendEmailVerification(cred.user);
      } catch (error) {
        // With "one account per email", signing up an email that already has a
        // Google account throws auth/email-already-in-use — handled here.
        const msg = authErrorMessage(error);
        if (msg) {
          alert(msg);
        }
      }
    },
    [email, password],
  );

  return (
    <>
      <div id="loginHeader">
        <div id="loginHeaderText">
          <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>
            Email Log In
          </h1>
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            Please login with your email and password.
          </p>
        </div>
        <div
          onClick={() => props.setLoginWithEmail(() => false)}
          id="backButtonContainer"
        >
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Back</p>
        </div>
      </div>
      <form id="loginForm">
        <div className="loginFormRowContainer">
          <div className="loginInputContainer">
            <label for="email">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: "300px" }}
              type="email"
              name="email"
            />
          </div>
          <div className="loginInputContainer">
            <label for="password">Password</label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: "300px" }}
              type="password"
              name="password"
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: "1rem" }}>
          <button onClick={handleEmailLogin} id="loginWithEmailButton">
            Log In
          </button>
          <button onClick={handleEmailSignUp} id="signUpWithEmailButton">
            Sign up
          </button>
        </div>
        <p
          className="forgotPasswordLink"
          onClick={() => props.onForgot()}
          style={{ paddingTop: "1rem" }}
        >
          Forgot password?
        </p>
      </form>
    </>
  );
};

// Unauthenticated password-reset view. Firebase sends the email and hosts the
// reset page. Used both inline (from "Forgot password?") and as the
// /reset-password route for direct linking.
const ResetPassword = (props) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [googleOnly, setGoogleOnly] = useState(false);
  const [error, setError] = useState("");

  const handleReset = useCallback(
    async (event) => {
      event.preventDefault();
      setError("");
      setGoogleOnly(false);
      try {
        // Google-sign-in accounts have no Firebase password, so a reset email
        // does nothing for them — their password lives in their Google account.
        // Detect that case up front and point them down the right branch.
        // NOTE: with email-enumeration protection enabled this returns [] and
        // we fall through to the normal (silent) send below — so it never
        // reveals which emails are registered (gotcha #16).
        let methods = [];
        try {
          methods = await fetchSignInMethodsForEmail(auth, email);
        } catch (_) {
          // Non-fatal — fall through to the normal send.
        }
        if (methods.includes("google.com") && !methods.includes("password")) {
          setGoogleOnly(true);
          return;
        }
        await sendPasswordResetEmail(auth, email);
        setSent(true);
      } catch (e) {
        if (e.code === "auth/invalid-email") {
          setError("Please enter a valid email address.");
          return;
        }
        // Treat a missing account like success so the form doesn't reveal
        // which emails are registered (gotcha #16).
        setSent(true);
      }
    },
    [email],
  );

  const goBack = () => (props.onBack ? props.onBack() : navigate("/login"));

  return (
    <>
      <div id="loginHeader">
        <div id="loginHeaderText">
          <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>
            Reset Password
          </h1>
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            Enter your email and we'll send you a reset link.
          </p>
        </div>
        <div onClick={goBack} id="backButtonContainer">
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Back</p>
        </div>
      </div>
      {googleOnly ? (
        <p
          style={{
            fontFamily: "Mulish",
            fontSize: "16px",
            fontWeight: "700",
            paddingTop: "1rem",
            textAlign: "center",
          }}
        >
          This account signs in with Google, so there's no password to reset
          here. Manage your password through your Google account, then use the
          "Sign In/Register with Google" button to log in.
        </p>
      ) : sent ? (
        <p
          style={{
            fontFamily: "Mulish",
            fontSize: "16px",
            fontWeight: "700",
            paddingTop: "1rem",
            textAlign: "center",
          }}
        >
          If an account exists for {email}, a reset link is on its way. Check
          your inbox.
        </p>
      ) : (
        <form id="loginForm" onSubmit={handleReset}>
          <div className="loginFormRowContainer">
            <div className="loginInputContainer">
              <label htmlFor="resetEmail">Email</label>
              <input
                id="resetEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: "300px" }}
                type="email"
                name="resetEmail"
              />
            </div>
          </div>
          {error && <p style={{ color: "red" }}>{error}</p>}
          <Button type="submit" disabled={!validateEmail(email)}>
            Send reset link
          </Button>
        </form>
      )}
    </>
  );
};


// Post-login redirect. Under the unified seat model there is no account-type
// choice: lazy doc-creation (Auth.js) guarantees a doc exists by the time
// authUserData resolves, and the destination adapts to capabilities, not a role
// string. Admin → /account; a valid access grant → /activity; otherwise
// → /account, where the user can buy seats or manage their team.
const SelectAccountType = () => {
  const { currentUser, authUserData } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser || !authUserData) {
      // Wait for the user doc (lazily created in Auth.js) to resolve.
      return;
    }
    if (authUserData.userType === "admin") {
      navigate("/account");
      return;
    }
    if (authUserData.hasAccess) {
      // Has a valid access grant → straight into the activities.
      navigate("/activity");
      return;
    }
    // No access yet: manage / buy.
    navigate("/account");
  }, [currentUser, authUserData, navigate]);

  return (
    <div className="loadingScreen">
      <SyncLoader color={"#e7b33e"} loading={true} size={20} />
    </div>
  );
};

// Signed-in email/password users start with emailVerified === false, and
// claimAccess (Auth.js) denies all access until it flips true. We send the
// verification email at sign-up; this screen waits for the user to click the
// link, auto-detects the flip (poll + reload), force-refreshes the ID token so
// its email_verified claim is current, and reloads through /login — which
// re-runs claimAccess and routes by capability. No manual re-login.
const VerifyEmail = () => {
  const { currentUser } = useContext(AuthContext);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const interval = setInterval(async () => {
      const user = auth.currentUser;
      if (!user) {
        return;
      }
      try {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(interval);
          // Force a fresh token so email_verified is current for claimAccess,
          // then full-reload through /login to re-resolve access + route.
          await user.getIdToken(true);
          window.location.assign("/login");
        }
      } catch (e) {
        // Transient (offline / token race) — keep polling.
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleResend = useCallback(async () => {
    setStatus("");
    try {
      await sendEmailVerification(auth.currentUser);
      setStatus("Verification email sent — check your inbox and spam folder.");
    } catch (e) {
      setStatus(
        e.code === "auth/too-many-requests"
          ? "Please wait a moment before requesting another email."
          : "Couldn't send the email. Please try again shortly.",
      );
    }
  }, []);

  return (
    <>
      <div id="loginHeader">
        <div id="loginHeaderText">
          <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>
            Verify your email
          </h1>
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            We've sent a verification link to {currentUser?.email}. Open it to
            activate your account — this page continues automatically once you
            do.
          </p>
        </div>
        <div onClick={() => signOut(auth)} id="backButtonContainer">
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Sign Out</p>
        </div>
      </div>
      <div id="loginBody">
        <SyncLoader color={"#e7b33e"} loading={true} size={15} />
        <button
          style={{ width: "300px", marginTop: "2rem" }}
          onClick={handleResend}
          id="emailLoginButton"
        >
          <p style={{ color: "white", marginTop: "15px", fontWeight: "700" }}>
            Resend verification email
          </p>
        </button>
        {status && (
          <p
            style={{
              paddingTop: "1rem",
              fontFamily: "Mulish",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            {status}
          </p>
        )}
      </div>
    </>
  );
};

// First-login Terms & Conditions gate. Shown exactly once — immediately after a
// user's first successful sign-in (Google, or a verified email account) — when
// their `users` doc has no terms_accepted flag. Accepting persists the flag and
// updates auth context, so returning users go straight through and never see
// this again.
const TermsGate = () => {
  const { currentUser, authUserData, setAuthUserData } = useContext(AuthContext);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!accepted || !currentUser) {
      return;
    }
    setSaving(true);
    await recordTermsAcceptance(currentUser);
    // Reflect acceptance in context so LogInScreen routes onward without a full
    // reload (recordTermsAcceptance already persisted it to Firestore).
    setAuthUserData({ ...authUserData, terms_accepted: true });
  }, [accepted, currentUser, authUserData, setAuthUserData]);

  return (
    <>
      <div id="loginHeader">
        <div id="loginHeaderText">
          <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>
            One last step
          </h1>
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            Please review and accept our Terms and Conditions to continue.
          </p>
        </div>
        <div onClick={() => signOut(auth)} id="backButtonContainer">
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Sign Out</p>
        </div>
      </div>
      <div id="loginBody">
        <TermsAndConditions accepted={accepted} onAccept={setAccepted} />
        <Button onClick={handleAccept} disabled={!accepted || saving}>
          {saving ? "Saving…" : "Accept and continue"}
        </Button>
      </div>
    </>
  );
};

const LogInScreen = ({ initialView = "select" }) => {
  const { currentUser, authUserData } = useContext(AuthContext);
  // "select" | "email" | "reset"
  const [view, setView] = useState(initialView);

  // A signed-in user runs a short gauntlet before being routed by capability.
  // The reset view is exempt — it's a self-contained flow reachable directly
  // via /reset-password even while signed in.
  if (currentUser && view !== "reset") {
    // Email/password users must verify before claimAccess grants anything; hold
    // them on the verify screen until then. Google sign-ins are always verified.
    if (!currentUser.emailVerified) {
      return <VerifyEmail />;
    }
    // Wait for the user doc (lazily created in Auth.js) before deciding whether
    // the first-login terms gate applies — otherwise SelectAccountType could
    // navigate away before the gate gets a chance to render.
    if (!authUserData) {
      return (
        <div className="loadingScreen">
          <SyncLoader color={"#e7b33e"} loading={true} size={20} />
        </div>
      );
    }
    // First login only: no terms_accepted flag yet → accept once, then route.
    if (!authUserData.terms_accepted) {
      return <TermsGate />;
    }
    return <SelectAccountType />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {view === "email" && (
        <EmailLogin
          setLoginWithEmail={() => setView("select")}
          onForgot={() => setView("reset")}
        />
      )}
      {view === "reset" && <ResetPassword onBack={() => setView("select")} />}
      {view === "select" && (
        <SelectLoginType
          setLoginWithEmail={() => setView("email")}
          onForgot={() => setView("reset")}
        />
      )}
    </div>
  );
};

export default LogInScreen;
